import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Pill, Sparkles, HeartPulse, Activity, TestTube2, Award, ArrowRight, Shield, Truck, Clock, Upload, Zap, Star, Phone, Quote, CheckCircle2, Stethoscope, Eye, Bone, Brain, Droplets, Wind, BadgeCheck, FlaskConical, ChevronRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import ProductCard from "@/components/ProductCard";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORY_ICONS = { Medicines: Pill, "Personal Care": Sparkles, "Health Conditions": HeartPulse, Devices: Activity, "Lab Tests": TestTube2, Brands: Award };
const HERO_IMAGES = [
  { title: "Your Health, Our Priority", subtitle: "Get genuine medicines at up to 40% off with doorstep delivery across India", img: "https://images.pexels.com/photos/14797859/pexels-photo-14797859.jpeg", cta: "Shop Now" },
  { title: "Upload Prescription", subtitle: "Simply upload your prescription and we'll deliver your medicines to your doorstep", img: "https://images.unsplash.com/photo-1603039827000-30f55dd00ea7", cta: "Upload Now" },
  { title: "Health Devices", subtitle: "Track your health at home with premium monitoring devices at best prices", img: "https://images.unsplash.com/photo-1544991875-5dc1b05f607d", cta: "Explore Devices" },
];
const PROMOS = [
  { title: "Flat 25% OFF", sub: "On first order", gradient: "from-cyan-500 to-teal-500", code: "WELCOME20" },
  { title: "Up to 43% OFF", sub: "Health devices", gradient: "from-indigo-500 to-purple-500" },
  { title: "Free Delivery", sub: "Orders Rs.499+", gradient: "from-emerald-500 to-green-500", code: "FREEDELIVERY" },
  { title: "Flat Rs.50 OFF", sub: "Orders Rs.500+", gradient: "from-amber-500 to-orange-500", code: "FLAT50" },
];
const TESTIMONIALS = [
  { name: "Rahul Sharma", city: "Mumbai", text: "Pharma Palace has been a lifesaver. Genuine medicines at great prices with super fast delivery. The substitute suggestions saved me so much!", rating: 5 },
  { name: "Priya Patel", city: "Delhi", text: "Very easy to use website. I uploaded my prescription and got my medicines delivered the next day. Great customer service too.", rating: 5 },
  { name: "Dr. Amit Kumar", city: "Bangalore", text: "As a doctor, I recommend Pharma Palace to my patients. The medicines are 100% genuine and the prices are very competitive.", rating: 5 },
  { name: "Sneha Reddy", city: "Hyderabad", text: "Best online pharmacy I've used. The health articles are very helpful and the prescription upload feature is so convenient!", rating: 4 },
  { name: "Vikram Singh", city: "Pune", text: "Ordered medicines for my parents. The delivery was on time and everything was well packaged. Will definitely order again.", rating: 5 },
  { name: "Anita Desai", city: "Chennai", text: "Great app with affordable medicines. The coupon codes give extra savings. Much better than buying from local pharmacy.", rating: 4 },
];
const ARTICLES = [
  { title: "10 Foods That Boost Your Immunity Naturally", category: "Nutrition", img: "https://images.unsplash.com/photo-1490645935967-10de6ba17061?w=400", time: "5 min" },
  { title: "Understanding Diabetes: Prevention & Management", category: "Health", img: "https://images.unsplash.com/photo-1559757175-5700dde675bc?w=400", time: "8 min" },
  { title: "Essential Vitamins Every Adult Should Take", category: "Wellness", img: "https://images.unsplash.com/photo-1584308666744-24d5c474f2ae?w=400", time: "4 min" },
  { title: "Home Remedies for Common Cold & Cough", category: "Remedies", img: "https://images.unsplash.com/photo-1576091160399-112ba8d25d1d?w=400", time: "6 min" },
];
const FAQS = [
  { q: "Are all medicines on Pharma Palace genuine?", a: "Yes, absolutely. All medicines are sourced directly from licensed manufacturers. We are a licensed pharmacy and guarantee 100% genuine, FDA-approved products." },
  { q: "How can I save on my medicine orders?", a: "Use coupon codes like WELCOME20 (20% off), FLAT50 (Rs.50 off), FREEDELIVERY. We also suggest affordable substitute medicines with the same salt composition." },
  { q: "Do I need a prescription to order medicines?", a: "Prescription medicines require a valid prescription uploaded during checkout. OTC medicines, personal care products, and health devices can be ordered without one." },
  { q: "What is your delivery time?", a: "Express delivery in 2 hours, Standard within 24 hours, or Scheduled delivery at your preferred time. Free delivery on orders above Rs.499." },
  { q: "Can I return medicines?", a: "We offer 15-day easy returns. Unopened medicine strips/bottles can be returned. Check our detailed return policy for specifics." },
  { q: "Is buying medicines online safe?", a: "Yes! We're a licensed pharmacy. All medicines are stored in temperature-controlled facilities and delivered in tamper-proof packaging with proper cold chain." },
  { q: "How do I track my order?", a: "Track orders in real-time from 'My Orders'. Get status updates via notifications at every stage: Confirmed, Packed, Shipped, Out for Delivery, Delivered." },
];
const CONDITIONS = [
  { name: "Diabetes", icon: Droplets, color: "bg-blue-50 text-blue-600" },
  { name: "Heart Care", icon: HeartPulse, color: "bg-red-50 text-red-600" },
  { name: "Bone & Joint", icon: Bone, color: "bg-amber-50 text-amber-600" },
  { name: "Respiratory", icon: Wind, color: "bg-teal-50 text-teal-600" },
  { name: "Eye Care", icon: Eye, color: "bg-purple-50 text-purple-600" },
  { name: "Thyroid", icon: Stethoscope, color: "bg-indigo-50 text-indigo-600" },
  { name: "Skin Care", icon: Sparkles, color: "bg-pink-50 text-pink-600" },
  { name: "Mental Wellness", icon: Brain, color: "bg-emerald-50 text-emerald-600" },
];

function CountdownTimer() {
  const [time, setTime] = useState({ hours: 5, minutes: 42, seconds: 18 });
  useEffect(() => {
    const interval = setInterval(() => {
      setTime(prev => {
        let { hours, minutes, seconds } = prev;
        seconds--;
        if (seconds < 0) { seconds = 59; minutes--; }
        if (minutes < 0) { minutes = 59; hours--; }
        if (hours < 0) return { hours: 23, minutes: 59, seconds: 59 };
        return { hours, minutes, seconds };
      });
    }, 1000);
    return () => clearInterval(interval);
  }, []);
  const pad = n => String(n).padStart(2, '0');
  return (
    <div className="flex gap-2">
      {[["hours", time.hours], ["min", time.minutes], ["sec", time.seconds]].map(([label, val]) => (
        <div key={label} className="bg-slate-900 text-white rounded-xl px-3 py-2 text-center min-w-[52px]">
          <div className="text-lg font-bold tabular-nums">{pad(val)}</div>
          <div className="text-[10px] text-slate-400 uppercase">{label}</div>
        </div>
      ))}
    </div>
  );
}

export default function Home() {
  const [categories, setCategories] = useState([]);
  const [products, setProducts] = useState([]);
  const [brands, setBrands] = useState([]);
  const [heroIdx, setHeroIdx] = useState(0);

  useEffect(() => {
    axios.get(`${API}/categories`).then(r => setCategories(r.data)).catch(() => {});
    axios.get(`${API}/products?limit=20`).then(r => setProducts(r.data.products || [])).catch(() => {});
    axios.get(`${API}/brands`).then(r => setBrands(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    const interval = setInterval(() => setHeroIdx(i => (i + 1) % HERO_IMAGES.length), 5000);
    return () => clearInterval(interval);
  }, []);

  const featured = products.slice(0, 8);
  const flashSale = products.filter(p => p.discount_percent >= 20).slice(0, 4);
  const deals = products.filter(p => p.discount_percent >= 15).slice(0, 8);

  return (
    <div className="bg-sky-50 overflow-x-hidden" data-testid="home-page">
      {/* Prescription Upload CTA Bar */}
      <section className="bg-gradient-to-r from-cyan-600 to-teal-600" data-testid="prescription-cta-bar">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3 flex flex-col sm:flex-row items-center justify-between gap-2">
          <div className="flex items-center gap-3 text-white">
            <Upload className="w-5 h-5" />
            <span className="text-sm font-medium">Upload your prescription & get medicines delivered</span>
          </div>
          <div className="flex items-center gap-3">
            <Link to="/products"><Button size="sm" className="bg-white text-cyan-700 hover:bg-cyan-50 rounded-full text-xs font-semibold px-4" data-testid="cta-upload-rx">Upload Prescription</Button></Link>
            <a href="tel:+911800123456" className="flex items-center gap-1 text-white text-xs font-medium hover:underline"><Phone className="w-3.5 h-3.5" /> 1800-123-456</a>
          </div>
        </div>
      </section>

      {/* Hero Section */}
      <section className="relative overflow-hidden hero-gradient" data-testid="hero-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 lg:py-16">
          {/* Mobile Hero Image Carousel */}
          <div className="lg:hidden mb-6 hero-carousel rounded-2xl overflow-hidden shadow-xl">
            <div className="relative aspect-[16/9]">
              <img src={HERO_IMAGES[heroIdx].img} alt="Hero" className="w-full h-full object-cover animate-heroSlide" key={heroIdx} />
              <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent" />
              <div className="absolute bottom-4 left-4 right-4">
                <h2 className="text-white text-xl font-bold drop-shadow-lg">{HERO_IMAGES[heroIdx].title}</h2>
                <p className="text-white/80 text-xs mt-1">{HERO_IMAGES[heroIdx].subtitle}</p>
              </div>
              {/* Carousel Dots */}
              <div className="absolute bottom-2 right-4 flex gap-1.5">
                {HERO_IMAGES.map((_, i) => (<button key={i} onClick={() => setHeroIdx(i)} className={`w-1.5 h-1.5 rounded-full transition-all ${i === heroIdx ? 'w-5 bg-white' : 'bg-white/50'}`} />))}
              </div>
            </div>
          </div>

          <div className="grid lg:grid-cols-2 gap-8 items-center">
            <div className="animate-fadeUp">
              <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-cyan-100 text-cyan-700 text-xs font-semibold rounded-full mb-4"><Zap className="w-3 h-3" /> Free Delivery on orders above Rs.499</span>
              <h1 className="text-3xl sm:text-4xl lg:text-5xl xl:text-6xl font-bold text-slate-800 tracking-tight leading-tight mb-4">{HERO_IMAGES[heroIdx].title}</h1>
              <p className="text-sm lg:text-lg text-slate-600 mb-6 max-w-lg">{HERO_IMAGES[heroIdx].subtitle}</p>
              <div className="flex gap-3">
                <Link to="/products"><Button className="bg-cyan-600 hover:bg-cyan-700 text-white rounded-full px-6 sm:px-8 py-3 font-semibold text-sm shadow-lg hover:shadow-xl transition-all active:scale-95" data-testid="hero-shop-btn">{HERO_IMAGES[heroIdx].cta} <ArrowRight className="w-4 h-4 ml-1" /></Button></Link>
                <Link to="/products"><Button variant="outline" className="rounded-full px-4 sm:px-6 py-3 border-cyan-200 text-cyan-700 hover:bg-cyan-50 font-semibold text-sm"><Upload className="w-4 h-4 mr-1" /> Upload Rx</Button></Link>
              </div>
            </div>
            {/* Desktop Hero Image with 3D Float */}
            <div className="relative hidden lg:block">
              <div className="card-3d">
                <div className="card-3d-inner rounded-3xl overflow-hidden shadow-2xl aspect-[4/3]">
                  <img src={HERO_IMAGES[heroIdx].img} alt="Hero" className="w-full h-full object-cover animate-heroSlide" key={heroIdx} />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent" />
                </div>
              </div>
              <div className="absolute -bottom-4 left-0 bg-white rounded-2xl p-4 shadow-xl animate-float"><div className="flex items-center gap-3"><div className="w-10 h-10 rounded-full bg-emerald-100 flex items-center justify-center"><Shield className="w-5 h-5 text-emerald-600" /></div><div><p className="text-sm font-semibold text-slate-800">100% Genuine</p><p className="text-xs text-slate-500">All products verified</p></div></div></div>
              <div className="absolute top-0 right-0 bg-white rounded-2xl p-3 shadow-xl animate-float stagger-3"><div className="flex items-center gap-2"><div className="w-8 h-8 rounded-full bg-cyan-100 flex items-center justify-center"><Truck className="w-4 h-4 text-cyan-600" /></div><div><p className="text-xs font-semibold text-slate-800">Express 2hr</p><p className="text-[10px] text-slate-500">Delivery</p></div></div></div>
            </div>
          </div>
          {/* Desktop dots */}
          <div className="hidden lg:flex justify-center gap-2 mt-10">
            {HERO_IMAGES.map((_, i) => (<button key={i} onClick={() => setHeroIdx(i)} className={`w-2 h-2 rounded-full transition-all ${i === heroIdx ? 'w-8 bg-cyan-600' : 'bg-slate-300'}`} />))}
          </div>
        </div>
      </section>

      {/* Why Choose Pharma Palace - Trust Strip */}
      <section className="bg-white border-y border-slate-100" data-testid="why-choose-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            {[
              { icon: BadgeCheck, title: "Safe", desc: "FDA & GMP certified medicines", color: "text-emerald-600 bg-emerald-50" },
              { icon: FlaskConical, title: "Same Composition", desc: "Exact same salt & dosage", color: "text-cyan-600 bg-cyan-50" },
              { icon: Zap, title: "Up to 40% Savings", desc: "Best prices guaranteed", color: "text-amber-600 bg-amber-50" },
              { icon: Stethoscope, title: "Doctor Verified", desc: "Every order reviewed", color: "text-indigo-600 bg-indigo-50" },
            ].map(({ icon: Icon, title, desc, color }) => (
              <div key={title} className="flex items-center gap-3 p-3">
                <div className={`w-11 h-11 rounded-2xl ${color} flex items-center justify-center shrink-0`}><Icon className="w-5 h-5" /></div>
                <div><p className="text-sm font-semibold text-slate-800">{title}</p><p className="text-[11px] text-slate-500">{desc}</p></div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Promotional Banners - 3D Cards */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8" data-testid="promo-banners">
        <div className="flex gap-3 overflow-x-auto pb-2 scrollbar-hide sm:grid sm:grid-cols-2 lg:grid-cols-4 sm:overflow-visible">
          {PROMOS.map((p, i) => (
            <Link key={i} to={p.code ? `/products` : "/products?category=Devices"} className={`flex-shrink-0 w-40 sm:w-auto bg-gradient-to-br ${p.gradient} rounded-2xl p-4 sm:p-5 text-white promo-card`}>
              <p className="text-base sm:text-lg font-bold">{p.title}</p>
              <p className="text-white/80 text-[11px] mt-0.5">{p.sub}</p>
              {p.code && <span className="inline-block mt-2 px-2 py-0.5 bg-white/20 rounded-full text-[10px] font-mono">Code: {p.code}</span>}
            </Link>
          ))}
        </div>
      </section>

      {/* Categories */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="categories-section">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-2xl sm:text-3xl font-bold text-slate-800 tracking-tight">Shop by Category</h2>
          <Link to="/products" className="text-sm font-semibold text-cyan-600 flex items-center gap-1">View all <ChevronRight className="w-4 h-4" /></Link>
        </div>
        <div className="grid grid-cols-3 sm:grid-cols-6 gap-4">
          {categories.map((cat, i) => {
            const Icon = CATEGORY_ICONS[cat.name] || Pill;
            return (
              <Link key={cat.category_id} to={`/products?category=${encodeURIComponent(cat.name)}`} className={`flex flex-col items-center gap-3 p-4 bg-white rounded-2xl shadow-[0_8px_30px_rgb(0,0,0,0.04)] border border-slate-100 hover:shadow-[0_20px_40px_rgb(8,145,178,0.08)] hover:-translate-y-1 transition-all animate-fadeUp stagger-${i + 1}`} data-testid={`category-${cat.slug}`}>
                <div className="w-14 h-14 rounded-2xl bg-cyan-50 flex items-center justify-center"><Icon className="w-7 h-7 text-cyan-600" /></div>
                <span className="text-xs font-semibold text-slate-700 text-center">{cat.name}</span>
                <span className="text-[10px] text-slate-400">{cat.subcategories?.length || 0} types</span>
              </Link>
            );
          })}
        </div>
      </section>

      {/* Flash Sale */}
      {flashSale.length > 0 && (
        <section className="bg-gradient-to-r from-cyan-600 to-indigo-600 py-10" data-testid="flash-sale-section">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-8">
              <div className="flex items-center gap-4"><Zap className="w-8 h-8 text-yellow-300" /><div><h2 className="text-2xl font-bold text-white">Flash Sale</h2><p className="text-cyan-100 text-sm">Grab before it's gone!</p></div></div>
              <div className="flex items-center gap-3"><span className="text-white text-sm font-medium">Ends in</span><CountdownTimer /></div>
            </div>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">{flashSale.map(p => <ProductCard key={p.product_id} product={p} />)}</div>
          </div>
        </section>
      )}

      {/* Popular Items */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10" data-testid="featured-products">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-2"><Star className="w-5 h-5 text-amber-400 fill-amber-400" /><h2 className="text-2xl sm:text-3xl font-bold text-slate-800 tracking-tight">Popular Items</h2></div>
          <Link to="/products" className="text-sm font-semibold text-cyan-600 flex items-center gap-1">View All <ArrowRight className="w-4 h-4" /></Link>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">{featured.map(p => <ProductCard key={p.product_id} product={p} />)}</div>
      </section>

      {/* Health Conditions */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10" data-testid="health-conditions-section">
        <h2 className="text-2xl sm:text-3xl font-bold text-slate-800 tracking-tight mb-6">Shop by Health Condition</h2>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          {CONDITIONS.map(({ name, icon: Icon, color }) => (
            <Link key={name} to={`/products?search=${encodeURIComponent(name)}`} className="flex items-center gap-3 bg-white p-4 rounded-2xl shadow-[0_8px_30px_rgb(0,0,0,0.04)] border border-slate-100 hover:shadow-lg hover:-translate-y-1 transition-all">
              <div className={`w-12 h-12 rounded-2xl ${color} flex items-center justify-center shrink-0`}><Icon className="w-6 h-6" /></div>
              <div><p className="text-sm font-semibold text-slate-800">{name}</p><p className="text-[10px] text-slate-400">Browse medicines</p></div>
            </Link>
          ))}
        </div>
      </section>

      {/* Deals You'll Love */}
      {deals.length > 4 && (
        <section className="bg-white py-10" data-testid="deals-section">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-2"><HeartPulse className="w-5 h-5 text-red-500" /><h2 className="text-2xl sm:text-3xl font-bold text-slate-800">Deals You'll Love</h2></div>
              <Link to="/products?sort=discount" className="text-sm font-semibold text-cyan-600 flex items-center gap-1">View All <ArrowRight className="w-4 h-4" /></Link>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">{deals.slice(4, 8).map(p => <ProductCard key={p.product_id} product={p} />)}</div>
          </div>
        </section>
      )}

      {/* Top Brands */}
      {brands.length > 0 && (
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10" data-testid="brands-section">
          <h2 className="text-2xl sm:text-3xl font-bold text-slate-800 tracking-tight mb-6">Top Brands</h2>
          <div className="flex gap-4 overflow-x-auto pb-4 scrollbar-hide -mx-4 px-4 sm:mx-0 sm:px-0">
            {brands.map(brand => (
              <Link key={brand.brand_id} to={`/products?brand=${encodeURIComponent(brand.name)}`} className="flex-shrink-0 w-32 h-24 bg-white rounded-2xl shadow-[0_8px_30px_rgb(0,0,0,0.04)] border border-slate-100 flex items-center justify-center hover:shadow-lg hover:-translate-y-1 transition-all">
                <div className="text-center"><div className="w-10 h-10 rounded-full mx-auto mb-1 flex items-center justify-center text-white text-lg font-bold" style={{ backgroundColor: brand.color }}>{brand.name[0]}</div><span className="text-xs font-semibold text-slate-700">{brand.name}</span></div>
              </Link>
            ))}
          </div>
        </section>
      )}

      {/* Health Articles */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10" data-testid="articles-section">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-2xl sm:text-3xl font-bold text-slate-800 tracking-tight">Health Articles</h2>
          <span className="text-sm font-semibold text-cyan-600">View All</span>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {ARTICLES.map((a, i) => (
            <div key={i} className="bg-white rounded-2xl overflow-hidden shadow-[0_8px_30px_rgb(0,0,0,0.04)] border border-slate-100 hover:shadow-lg transition-all group cursor-pointer">
              <div className="aspect-video overflow-hidden"><img src={a.img} alt={a.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" loading="lazy" /></div>
              <div className="p-4">
                <span className="text-[10px] font-semibold text-cyan-600 uppercase tracking-wider">{a.category}</span>
                <h3 className="text-sm font-semibold text-slate-800 mt-1 line-clamp-2 leading-snug">{a.title}</h3>
                <div className="flex items-center justify-between mt-3">
                  <span className="text-[10px] text-slate-400"><Clock className="w-3 h-3 inline mr-0.5" />{a.time} read</span>
                  <span className="text-xs font-semibold text-cyan-600">Read More</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Customer Testimonials */}
      <section className="bg-white py-12" data-testid="testimonials-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl sm:text-3xl font-bold text-slate-800 tracking-tight text-center mb-2">What Our Customers Say</h2>
          <p className="text-sm text-slate-500 text-center mb-8">Trusted by 10 Lakh+ happy customers across India</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {TESTIMONIALS.slice(0, 6).map((t, i) => (
              <div key={i} className="bg-sky-50 rounded-2xl p-5 relative">
                <Quote className="w-8 h-8 text-cyan-200 absolute top-4 right-4" />
                <div className="flex gap-0.5 mb-3">{[1,2,3,4,5].map(s => <Star key={s} className={`w-4 h-4 ${s <= t.rating ? 'fill-amber-400 text-amber-400' : 'text-slate-300'}`} />)}</div>
                <p className="text-sm text-slate-600 leading-relaxed mb-4 line-clamp-3">{t.text}</p>
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 rounded-full bg-cyan-600 text-white flex items-center justify-center text-xs font-bold">{t.name[0]}</div>
                  <div><p className="text-sm font-semibold text-slate-800">{t.name}</p><p className="text-[10px] text-slate-500">{t.city}</p></div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Trust Section */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10" data-testid="trust-section">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {[
            { icon: Shield, title: "100% Genuine Medicines", desc: "All products sourced directly from licensed manufacturers & verified distributors. ISO 9001 certified pharmacy.", bgc: "bg-emerald-50", tc: "text-emerald-600" },
            { icon: Truck, title: "Fast & Free Delivery", desc: "Express 2-hour delivery. Standard 24-hour. Free delivery on all orders above Rs.499 across India.", bgc: "bg-cyan-50", tc: "text-cyan-600" },
            { icon: Clock, title: "24/7 Customer Support", desc: "Round the clock support via phone, email and chat. Licensed pharmacists available for guidance.", bgc: "bg-indigo-50", tc: "text-indigo-600" },
          ].map(({ icon: Icon, title, desc, bgc, tc }) => (
            <div key={title} className="flex items-start gap-4 bg-white rounded-2xl p-5 shadow-[0_8px_30px_rgb(0,0,0,0.04)] border border-slate-100">
              <div className={`w-12 h-12 rounded-2xl ${bgc} flex items-center justify-center shrink-0`}><Icon className={`w-6 h-6 ${tc}`} /></div>
              <div><h3 className="text-sm font-semibold text-slate-800 mb-1">{title}</h3><p className="text-xs text-slate-500">{desc}</p></div>
            </div>
          ))}
        </div>
      </section>

      {/* FAQ Section */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10" data-testid="faq-section">
        <h2 className="text-2xl sm:text-3xl font-bold text-slate-800 tracking-tight text-center mb-2">Frequently Asked Questions</h2>
        <p className="text-sm text-slate-500 text-center mb-8">Got questions? We've got answers</p>
        <div className="max-w-3xl mx-auto">
          <Accordion type="single" collapsible className="space-y-3">
            {FAQS.map((faq, i) => (
              <AccordionItem key={i} value={`faq-${i}`} className="bg-white rounded-2xl border border-slate-100 shadow-[0_4px_20px_rgb(0,0,0,0.03)] px-5 overflow-hidden">
                <AccordionTrigger className="text-sm font-semibold text-slate-800 hover:text-cyan-600 py-4 hover:no-underline" data-testid={`faq-trigger-${i}`}>{faq.q}</AccordionTrigger>
                <AccordionContent className="text-sm text-slate-600 pb-4 leading-relaxed">{faq.a}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* Newsletter + App Download CTA */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-12">
        <div className="bg-gradient-to-r from-cyan-600 to-cyan-700 rounded-3xl p-8 lg:p-12">
          <div className="grid lg:grid-cols-2 gap-8 items-center">
            <div>
              <h2 className="text-2xl lg:text-3xl font-bold text-white mb-2">Download the Pharma Palace App</h2>
              <p className="text-cyan-100 mb-4">Get exclusive app-only offers, track orders on the go, and save up to 40% on every order</p>
              <div className="flex gap-2 mt-2 flex-wrap">
                <div className="bg-white/10 backdrop-blur-sm rounded-xl px-5 py-3 text-white text-sm font-medium border border-white/20 cursor-pointer hover:bg-white/20 transition-colors">App Store</div>
                <div className="bg-white/10 backdrop-blur-sm rounded-xl px-5 py-3 text-white text-sm font-medium border border-white/20 cursor-pointer hover:bg-white/20 transition-colors">Google Play</div>
              </div>
              <div className="flex items-center gap-3 mt-4 text-white/80 text-xs flex-wrap">
                <span className="flex items-center gap-1"><CheckCircle2 className="w-3 h-3" /> 10L+ downloads</span>
                <span className="flex items-center gap-1"><Star className="w-3 h-3 fill-current" /> 4.8 rating</span>
                <span className="flex items-center gap-1"><Shield className="w-3 h-3" /> 100% secure</span>
              </div>
            </div>
            <div>
              <p className="text-white font-semibold text-sm mb-3">Subscribe to health tips & offers</p>
              <div className="flex gap-2 flex-col sm:flex-row">
                <input type="email" placeholder="Enter your email" className="flex-1 px-4 py-3 bg-white/10 border border-white/20 rounded-full text-white placeholder:text-white/50 text-sm focus:outline-none focus:ring-2 focus:ring-white/30" data-testid="newsletter-email" />
                <Button className="bg-white text-cyan-700 hover:bg-cyan-50 rounded-full px-6 font-semibold text-sm shrink-0" data-testid="newsletter-subscribe">Subscribe</Button>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
